import React from "react";
import { Link } from "react-router-dom";

import Constants from "../helpers/Constants";

// Footer bg is dark, so we always render the white silhouette variant of the
// brand mark. Bundled via webpack so src/img/ edits are picked up.
import logoWhite from "../img/landing/logo_growingsales-white.svg";
// 우진 (2026-09-09): 라이트 푸터(Figma footer-light 1562:20874, /playbook 전용)
// 자산 — 모노 마크(Subtract 18.92×18.92 #424952) + 영문 워드마크(109.8×16.56
// #424952) + 흰색 SNS glyph(24×24). src/img/playbook/ 에서 webpack 번들.
import logoLightMark from "../img/playbook/light-logo-subtract.svg";
import logoLightWordmark from "../img/playbook/light-wordmark.svg";
import snsLightInsta from "../img/playbook/light-sns-insta.svg";
import snsLightYoutube from "../img/playbook/light-sns-youtube.svg";
import snsLightNaver from "../img/playbook/light-sns-naver.svg";

// ---------------------------------------------------------------------------
// Global Footer — Figma node 249:22192
//
// Layout:
//   Logo (mark + GrowingSales wordmark)
//   ─────────────────────────────────────────────────────────────────
//   FAQ · 고객센터 · 카페24 스토어(↗) · 아임웹 스토어(↗) · 도입문의    개인정보처리방침(밑줄) · 서비스이용약관
//   ─────────────────────────────────────────────────────────────────
//   © 그로잉세일즈 주식회사 · 사업자번호: 376-87-01493 · 대표자: 정우진    [SNS][SNS][SNS]
//   서울특별시 마포구 동교로27길 3-12
//
//   Growing Sales Inc. Ⓒ All right reserved.
// ---------------------------------------------------------------------------

// 우진 (2026-04-30): footer 링크들을 실제 외부 URL/내부 라우트로 매핑.
//   고객센터    → support.thegrowing.co (external)
//   카페24      → 앱 상세 페이지 (external + chevron)
//   아임웹      → 앱스토어 상세 페이지 (external + chevron) — 2026-09-09 추가
//   도입문의    → 채널톡 (external, CHANNEL_TALK)
//   이용약관    → /policy/eula  (기존 /policy/terms 에서 수정)
//   개인정보처리방침 → /policy/privacy
//   활용백서    → /playbook (internal Link, chevron 없음) — 2026-09-09 추가,
//                 Figma 1585:31149 / 1562:20874 의 primary row 맨 오른쪽(1562:20684).
// suspect: Figma footer shows FAQ and no 아임웹 스토어; existing link set kept
// (FAQ 미추가, 아임웹 스토어 유지) — 활용백서만 마지막에 추가.
// 참고: 아임웹 스토어 링크(+ _site-footer.scss 의 __links 줄바꿈 규칙)는 활용백서
// 작업 이전부터 working tree 에 있던 미커밋 변경(HEAD 에는 없음) — 이 작업에서
// 추가/수정하지 않았고 그대로 둠.
const PRIMARY_LINKS = [
  { label: "고객센터",     to: "https://support.thegrowing.co/", external: true },
  { label: "카페24 스토어", to: "https://store.cafe24.com/kr/apps/22979", external: true, chevron: true },
  { label: "아임웹 스토어", to: "https://imweb.me/appstore/app?app=ga2026033141f2189951ccc", external: true, chevron: true },
  { label: "도입문의",     to: Constants.CHANNEL_TALK, external: true },
  { label: "활용백서",     to: "/playbook" },
];

const POLICY_LINKS = [
  { label: "개인정보처리방침", to: "/policy/privacy", underline: true },
  { label: "서비스이용약관",   to: "/policy/eula" },
];

// SNS glyphs (Instagram / YouTube / Naver Blog) come from pre-baked asset files
// in /img/landing/ whose fill is already #EFEFF4 — i.e. sitting correctly on
// the footer's dark bg — so we just drop them in as <img> with no recoloring.
// chevron-up-right is still inline because it inherits currentColor from the
// link text (white on footer).
function SnsIcon({ name, light }) {
  // 우진 (2026-09-09): light variant — Figma 1562:20874 sns glyph fill #FFF,
  // inner frame 24×24 (src/img/playbook/light-sns-*.svg). dark 는 기존 그대로.
  if (light) {
    switch (name) {
      case "instagram":
        return <img src={snsLightInsta} alt="" aria-hidden="true" width="24" height="24" loading="lazy" />;
      case "youtube":
        return <img src={snsLightYoutube} alt="" aria-hidden="true" width="24" height="24" loading="lazy" />;
      case "naver-blog":
        return <img src={snsLightNaver} alt="" aria-hidden="true" width="24" height="24" loading="lazy" />;
      default:
        break;
    }
  }
  switch (name) {
    case "instagram":
      return <img src="/img/landing/instagram.svg" alt="" aria-hidden="true" width="19" height="19" loading="lazy" />;
    case "youtube":
      return <img src="/img/landing/youtube.svg" alt="" aria-hidden="true" width="22" height="16" loading="lazy" />;
    case "naver-blog":
      return <img src="/img/landing/naver.svg" alt="" aria-hidden="true" width="17" height="15" loading="lazy" />;
    case "chevron-up-right":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M7 17L17 7M8 7h9v9" />
        </svg>
      );
    default:
      return null;
  }
}

function FooterLogo({ light }) {
  // 우진 (2026-09-09): light variant — Figma 1562:20874 logo: Subtract mark
  // 18.92×18.92 + GrowingSales wordmark 109.8×16.56 (both #424952), absolute
  // composition (wordmark at left 27.2 / top 2.36 of the mark's origin →
  // total ≈137×18.92). Offsets reproduced in _site-footer.scss
  // (.site-footer--light .site-footer__logo) as read — see suspect S12.
  if (light) {
    return (
      <span className="site-footer__logo site-footer__logo--light" aria-label="GrowingSales">
        <img
          className="site-footer__logo-mark site-footer__logo-mark--light"
          src={logoLightMark}
          alt=""
          aria-hidden="true"
          width="18.92"
          height="18.92"
        />
        <img
          className="site-footer__wordmark-img"
          src={logoLightWordmark}
          alt=""
          aria-hidden="true"
          width="109.8"
          height="16.56"
        />
      </span>
    );
  }
  // White silhouette variant — the dark footer bg reads this directly.
  return (
    <span className="site-footer__logo" aria-label="GrowingSales">
      <img
        className="site-footer__logo-mark"
        src={logoWhite}
        alt=""
        aria-hidden="true"
        width="32"
        height="32"
      />
      <span className="site-footer__wordmark">그로잉세일즈</span>
    </span>
  );
}

// 우진 (2026-09-09): `variant` — "dark"(기본, Figma 1585:31149) | "light"
// (Figma 1562:20874, /playbook 전용, App.js 에서 라우트 prefix 로 분기).
// light 는 .site-footer--light modifier + 로고/SNS 자산 스왑만 다르고
// 마크업/링크/동작은 dark 와 동일.
export default function Footer({ variant = "dark" }) {
  const light = variant === "light";
  return (
    <footer className={light ? "site-footer site-footer--light" : "site-footer"}>
      <div className="site-footer__inner">
        <Link to="/" className="site-footer__brand">
          <FooterLogo light={light} />
        </Link>

        <div className="site-footer__body">
          <div className="site-footer__menu">
            <ul className="site-footer__links">
              {PRIMARY_LINKS.map((l) => (
                <li key={l.label}>
                  {l.external ? (
                    <a href={l.to} target="_blank" rel="noopener noreferrer">
                      <span>{l.label}</span>
                      {l.chevron && <SnsIcon name="chevron-up-right" />}
                    </a>
                  ) : (
                    <Link to={l.to}><span>{l.label}</span></Link>
                  )}
                </li>
              ))}
            </ul>
            <ul className="site-footer__links site-footer__links--policy">
              {POLICY_LINKS.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className={l.underline ? "site-footer__link--underline" : ""}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <span className="site-footer__divider" />

          <div className="site-footer__info-row">
            <div className="site-footer__info">
              <div className="site-footer__info-line">
                <span>© 그로잉세일즈 주식회사</span>
                <span className="site-footer__info-sep" />
                <span>사업자번호: 376-87-01493</span>
                <span className="site-footer__info-sep" />
                <span>대표자: 정우진</span>
              </div>
              <p className="site-footer__address">서울특별시 마포구 동교로27길 3-12 지층</p>
            </div>

            {/* 우진 (2026-04-30): 실제 SNS 계정 URL 로 매핑.
                  Instagram: growing.sales (점 포함)
                  YouTube  : @growingsales
                  Naver    : growingco */}
            <div className="site-footer__sns">
              <a href="https://www.instagram.com/growing.sales" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="site-footer__sns-icon">
                <SnsIcon name="instagram" light={light} />
              </a>
              <a href="https://www.youtube.com/@growingsales" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="site-footer__sns-icon">
                <SnsIcon name="youtube" light={light} />
              </a>
              <a href="https://blog.naver.com/growingco" target="_blank" rel="noopener noreferrer" aria-label="네이버 블로그" className="site-footer__sns-icon">
                <SnsIcon name="naver-blog" light={light} />
              </a>
            </div>
          </div>

          <p className="site-footer__copy">Growing Sales Inc. Ⓒ All right reserved.</p>
        </div>
      </div>
    </footer>
  );
}
